import {
  CheckboxField,
  AlertIcon,
  Flex,
  Image,
  Popover,
  SortUpIcon,
  Typography,
} from '@iheartjane/reefer';
import { pluralize } from '@iheartjane/utils';
import { createColumnHelper } from '@tanstack/react-table';
import type {
  CellData,
  Column,
  ColumnDef,
  RowData,
} from '@tanstack/react-table';
import capitalize from 'lodash-es/capitalize';
import omit from 'lodash-es/omit';
import { useMemo } from 'react';

import { labelForVal } from '@jane/shared/util';

import { lineageLabel } from './lineageLabel';
import { priceWeightLabel } from './priceWeightLabel';
import type { Filters, Product, ProductFilters } from './productSelect.types';
import {
  getSelectedProductIds,
  INVENTORY_FILTER_VALUES,
} from './productSelect.util';
import type { ProductSelectFeatures } from './productSelectFeatures';

const SortableHeader = <R extends RowData, TValue extends CellData>({
  column,
  label,
}: {
  column: Column<ProductSelectFeatures, R, TValue>;
  label: string;
}) => {
  const sortByAsc = column.getIsSorted() === 'asc';
  const isSorted = sortByAsc || column.getIsSorted() === 'desc';
  const toggleSort = () => column.toggleSorting(sortByAsc);

  return (
    <Flex
      alignItems="center"
      onClick={toggleSort}
      aria-label={`Sort by ${sortByAsc ? 'ascending' : 'descending'}`}
      width="100%"
    >
      <Typography color="grays-mid" variant="caps-regular" truncateAt="100%">
        {label}
      </Typography>
      <div>
        <SortUpIcon
          color={isSorted ? 'primary-dark' : 'primary-light'}
          rotate={sortByAsc ? 'up' : 'down'}
        />
      </div>
    </Flex>
  );
};

/**
 * The shared product table's column set. `R` is the consumer's own row type, so
 * any row carrying the fields these columns read works without a cast.
 */
export const useProductSelectColumns = <R extends Partial<Product>>({
  allProductIds,
  filters,
  productCount,
  selectable = true,
  showSelectAllCheckbox = true,
  unfilterable,
}: {
  /** Every matching id, including rows on pages that haven't loaded */
  allProductIds?: string[];
  filters: Filters;
  productCount: number;
  selectable?: boolean;
  showSelectAllCheckbox?: boolean;
  /** Columns to render without a filter, for a catalog that can't serve one */
  unfilterable?: ProductFilters[];
}): ColumnDef<ProductSelectFeatures, R>[] => {
  const columnHelper = useMemo(
    () => createColumnHelper<ProductSelectFeatures, R>(),
    []
  );

  return useMemo<ColumnDef<ProductSelectFeatures, R>[]>(() => {
    const selectColumn = columnHelper.display({
      id: 'select',
      header: ({ table }) => {
        if (!showSelectAllCheckbox) {
          return;
        }
        /**
         * NOTE: When "selecting all", if allProductIds is present then we rely on that list
         * to make selections, if it isn't present then we only select all the VISIBLE items,
         * so if the results are paginated then all items will not be selected
         */
        const selectedIds = new Set(table.getSelectedRowIds());
        const isChecked = allProductIds
          ? allProductIds.length > 0 &&
            allProductIds.every((id) => selectedIds.has(id))
          : table.getIsAllRowsSelected();
        const isSomeSelected = allProductIds
          ? allProductIds.some((id) => selectedIds.has(id))
          : table.getIsSomeRowsSelected();
        const isIndeterminate = isSomeSelected && !isChecked;
        return (
          <CheckboxField
            checked={isChecked}
            indeterminate={isIndeterminate}
            // Applies the box's own state instead of toggling: CheckboxField
            // also fires this when `checked` changes externally, and a toggle
            // would flip that change straight back
            onChange={(nextChecked) => {
              if (!allProductIds) {
                table.toggleAllRowsSelected(nextChecked);
                return;
              }

              // Leaves selections outside the current matches untouched
              table.setRowSelection((current) =>
                nextChecked
                  ? { ...current, ...getSelectedProductIds(allProductIds) }
                  : omit(current, allProductIds)
              );
            }}
            label="Select all products"
            name="Select all products"
            labelHidden
          />
        );
      },
      cell: ({ row }) => {
        return (
          <CheckboxField
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            label={`Select product ${row.original.name}`}
            name={`Select product ${row.original.name}`}
            labelHidden
          />
        );
      },
      size: 72,
    });

    // An accessor function, since a generic row type can't resolve the 'name' key
    const nameColumn = columnHelper.accessor((row) => row.name, {
      id: 'name',
      header: (props) => (
        <SortableHeader
          // Only the noun goes through `pluralize` — its own count display
          // caps at "1,000+", and the header needs the real total
          label={`${productCount} ${pluralize({
            noun: 'product',
            number: productCount,
            returnValue: 'word',
          })}`}
          column={props.column}
        />
      ),
      enableColumnFilter: false,
      cell: ({ row }) => {
        return (
          <Flex alignItems="center" gap={16}>
            <Image
              height="48px"
              width="48px"
              border
              borderRadius="12px"
              sizes="thumbnail"
              responsive
              src={
                row.original.photos ? row.original.photos[0]?.urls.small : ''
              }
              altText={row.original.name ? row.original.name : ''}
            />

            <Typography truncateAt="275px">{row.original.name}</Typography>
          </Flex>
        );
      },
      size: 350,
    });

    const idColumn = columnHelper.accessor((row) => capitalize(row.id), {
      id: 'id',
      header: (props) => <SortableHeader label="Id" column={props.column} />,
      enableColumnFilter: false,
      size: 96,
    });

    const brandColumn = columnHelper.accessor((row) => capitalize(row.brand), {
      id: 'brand',
      header: (props) => <SortableHeader label="Brand" column={props.column} />,
      meta: {
        filterValues: filters.brand,
        filterLabel: 'Brand',
      },
    });

    const categoryColumn = columnHelper.accessor(
      (row) => capitalize(row.category),
      {
        id: 'category',
        header: (props) => (
          <SortableHeader label="Category" column={props.column} />
        ),
        meta: {
          filterValues: filters.category,
          filterLabel: 'Category',
        },
      }
    );

    const subcategoryColumn = columnHelper.accessor(
      (row) => capitalize(row.subcategory),
      {
        id: 'subcategory',
        header: (props) => (
          <SortableHeader label="Subcategory" column={props.column} />
        ),
        meta: {
          filterValues: filters.subcategory,
          filterLabel: 'Subcategory',
        },
      }
    );

    // Sorted on the low end of the range; the cell renders the whole span
    const weightColumn = columnHelper.accessor((row) => row.priceRange?.min, {
      id: 'weight',
      header: (props) => (
        <SortableHeader label="Price/Weight" column={props.column} />
      ),
      cell: ({ row }) => (
        <Typography truncateAt="100%">
          {priceWeightLabel(row.original.priceRange)}
        </Typography>
      ),
      enableColumnFilter: !unfilterable?.includes('weight'),
      meta: {
        filterValues: filters.weight,
        filterLabel: 'Weight',
        // Weights arrive as the price columns they filter on, ex `price_half_gram`
        filterOptionLabel: (value: string) => labelForVal(value) ?? value,
      },
    });

    const lineageColumn = columnHelper.accessor(
      (row) => lineageLabel(row.lineage),
      {
        id: 'lineage',
        header: (props) => (
          <SortableHeader label="Lineage" column={props.column} />
        ),
        meta: {
          filterValues: filters.lineage,
          filterLabel: 'Lineage',
          filterOptionLabel: lineageLabel,
        },
      }
    );

    // An accessor, not a display column, so the table will let it carry a filter
    const outOfStock = columnHelper.accessor((row) => row.outOfStock, {
      id: 'outOfStock',
      header: () => null,
      cell: ({ row }) =>
        row.original.outOfStock ? (
          <Popover
            target={<AlertIcon color="error" />}
            label="Out of stock"
            openOn="hover"
            alignment={{ horizontal: 'right' }}
          >
            <Popover.Content>
              <Typography>This product is out of stock</Typography>
            </Popover.Content>
          </Popover>
        ) : undefined,
      enableSorting: false,
      meta: {
        filterValues: INVENTORY_FILTER_VALUES,
        filterLabel: 'Inventory',
      },
      size: 72,
    });

    // columnHelper.columns() preserves each column's own value type; a plain
    // array widens them all and no longer satisfies the table's columns option.
    return columnHelper.columns([
      ...(selectable ? [selectColumn] : []),
      nameColumn,
      idColumn,
      brandColumn,
      categoryColumn,
      subcategoryColumn,
      weightColumn,
      lineageColumn,
      outOfStock,
    ]);
  }, [
    allProductIds,
    columnHelper,
    filters,
    productCount,
    selectable,
    unfilterable,
  ]);
};
